// External
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from '@ohif/i18n';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import Compose from './routes/Mode/Compose';

import { ViewportRefsProvider } from '@ohif/core';
import {
  DialogProvider,
  Modal,
  ModalProvider,
  SnackbarProvider,
  ViewportDialogProvider,
  ViewportGridProvider,
  CineProvider,
  PrintProvider,
  UserAuthenticationProvider,
  SidePanelProvider,
} from '@ohif/ui';
// Viewer Project
// TODO: Should this influence study list?
import { AppConfigProvider } from '@state';
import createRoutes from './routes';
import appInit from './appInit.js';
import OpenIdConnectRoutes from './utils/OpenIdConnectRoutes';
import { ConfigProvider, theme } from 'antd';
import { ThemeProvider } from '@vindr/ui';
import OAuthRoutes from './utils/OAuthRoutes';
import vi_VN from '@ohif/i18n/src/locales/vi/Antd_VI';
import en_US from 'antd/es/locale/en_US';
import { LANGUAGES } from '@vindr/services/constants/common.constant';

let commandsManager, extensionManager, servicesManager, hotkeysManager;

const antd_locales = {
  [LANGUAGES.VI]: vi_VN,
  [LANGUAGES.US]: en_US,
};

function App({ config, defaultExtensions, defaultModes }) {
  const [init, setInit] = useState(null);
  useEffect(() => {
    const run = async () => {
      appInit(config, defaultExtensions, defaultModes)
        .then(setInit)
        .catch(console.error);
    };

    run();
  }, []);

  useEffect(() => {
    const loadingPreload = document.querySelector('#loading-preload');
    if (!loadingPreload) return;

    document.body.removeChild(loadingPreload);
  }, []);

  useEffect(() => {
    const defaultLang = localStorage.getItem('defaultLanguage') || LANGUAGES.VI;

    i18n.changeLanguage(defaultLang);
  }, []);

  if (!init) {
    return null;
  }

  // Set above for named export
  commandsManager = init.commandsManager;
  extensionManager = init.extensionManager;
  servicesManager = init.servicesManager;
  hotkeysManager = init.hotkeysManager;

  // Set appConfig
  const appConfigState = init.appConfig;
  const { routerBasename, modes, dataSources, oidc, oauth, showStudyList } =
    appConfigState;

  const {
    UIDialogService,
    uiModalService,
    UINotificationService,
    UIViewportDialogService,
    ViewportGridService,
    CineService,
    PrintService,
    userAuthenticationService,
    customizationService,
    SidePanelService,
  } = servicesManager.services;

  const providers = [
    [AppConfigProvider, { value: appConfigState }],
    [UserAuthenticationProvider, { service: userAuthenticationService }],
    [I18nextProvider, { i18n }],
    [ThemeProvider],
    [SidePanelProvider, { service: SidePanelService }],
    [ViewportRefsProvider],
    [ViewportGridProvider, { service: ViewportGridService }],
    [ViewportDialogProvider, { service: UIViewportDialogService }],
    [CineProvider, { service: CineService }],
    [PrintProvider, { service: PrintService }],
    [SnackbarProvider, { service: UINotificationService }],
    [DialogProvider, { service: UIDialogService }],
    [ModalProvider, { service: uiModalService, modal: Modal }],
    [
      ConfigProvider,
      {
        locale: antd_locales[i18n.language],
        theme: {
          algorithm: theme.darkAlgorithm,
          token: {
            fontSize: 12,
            borderRadiusSM: 2,
            borderRadiusLG: 2,
            borderRadius: 2,
            colorPrimary: '#39C2D7',
            colorPrimaryHover: '#2799b0',
            colorText: '#fff',
            colorBgBase: '#172126',
            colorBgContainer: 'transparent',
            colorBorder: '#647a87',
            controlItemBgActiveDisabled: '#2C4654',
            controlItemBgActive: '#2C4654',
            controlOutlineWidth: 0,
            colorBgElevated: '#1F313B',
          },
          components: {
            Menu: {
              colorItemBg: '#082434',
              colorItemBgSelected: '#2c5166',
              colorSubItemBg: '#082434',
            },
          },
        },
      },
    ],
  ];
  const CombinedProviders = ({ children }) =>
    Compose({ components: providers, children });

  let authRoutes = null;

  // Should there be a generic call to init on the extension manager?
  customizationService.init(extensionManager);

  // Use config to create routes
  const appRoutes = createRoutes({
    modes,
    dataSources,
    extensionManager,
    servicesManager,
    commandsManager,
    hotkeysManager,
    routerBasename,
    showStudyList,
  });

  if (oauth) {
    authRoutes = (
      <OAuthRoutes
        oauth={oauth}
        routerBasename={routerBasename}
        userAuthenticationService={userAuthenticationService}
      />
    );
  } else if (oidc) {
    authRoutes = (
      <OpenIdConnectRoutes
        oidc={oidc}
        routerBasename={routerBasename}
        userAuthenticationService={userAuthenticationService}
      />
    );
  }

  return (
    <CombinedProviders>
      <BrowserRouter basename={routerBasename}>
        {authRoutes}
        {appRoutes}
      </BrowserRouter>
    </CombinedProviders>
  );
}

App.propTypes = {
  config: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      routerBasename: PropTypes.string.isRequired,
      oidc: PropTypes.array,
      whiteLabeling: PropTypes.object,
      extensions: PropTypes.array,
    }),
  ]).isRequired,
  /* Extensions that are "bundled" or "baked-in" to the application.
   * These would be provided at build time as part of they entry point. */
  defaultExtensions: PropTypes.array,
};

App.defaultProps = {
  config: {
    /**
     * Relative route from domain root that OHIF instance is installed at.
     * For example:
     *
     * Hosted at: https://ohif.org/where-i-host-the/viewer/
     * Value: `/where-i-host-the/viewer/`
     * */
    routerBaseName: '/',
    /**
     *
     */
    showLoadingIndicator: true,
    showStudyList: true,
    oidc: [],
    extensions: [],
  },
  defaultExtensions: [],
};

export default App;

export { commandsManager, extensionManager, servicesManager };
